/**
 * Maps master salutation titles (from salutationdetails) to BizOps / eOPS codes (MR, MISS, …).
 */
export function mapSalutationTitleToBizOps(salutationTitle) {
  if (!salutationTitle || typeof salutationTitle !== "string") return "";
  const t = salutationTitle.trim().toUpperCase();
  if (t.includes("MISS")) return "MISS";
  if (t.includes("MRS")) return "MRS";
  if (t.includes("MS") && !t.includes("MRS") && !t.includes("MISS")) return "MS";
  if (t.startsWith("MR")) return "MR";
  return "";
}

/**
 * Nominee and appointee dropdown options from relationshipdetails.
 * A row is included only when showRelationInNomineeOrAppointee is true.
 */
export function toNomineeRelationshipOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((item) => item?.showRelationInNomineeOrAppointee === true)
    .map((item) => ({
      label: item.relation,
      value: item.relation,
    }));
}

/**
 * Maps master relationship labels (from relationshipdetails) to BizOps 2-letter codes (SP, BR, …).
 */
export function mapRelationLabelToBizOps(relation) {
  if (!relation || typeof relation !== "string") return "";
  const u = relation.trim().toUpperCase();
  if (u === "SELF" || u.includes("SELF")) return "SE";
  if (u.includes("SPOUSE") || u.includes("HUSBAND") || u.includes("WIFE")) return "SP";
  if (u.includes("BROTHER")) return "BR";
  if (u.includes("SISTER")) return "SR";
  if (u.includes("FATHER") && !u.includes("IN LAW") && !u.includes("IN-LAW")) return "FA";
  if (u.includes("MOTHER") && !u.includes("IN LAW") && !u.includes("IN-LAW")) return "MO";
  if (u.includes("SON")) return "SO";
  if (u.includes("DAUGHTER")) return "DA";
  if (u.includes("GRANDSON")) return "GS";
  if (u.includes("GRANDDAUGHTER")) return "GD";
  if (u.includes("UNCLE")) return "UN";
  if (u.includes("AUNT")) return "AU";
  if (u.includes("NEPHEW")) return "NP";
  if (u.includes("NIECE")) return "NC";
  return "";
}

/**
 * Enriches nominee rows with BizOps string codes derived from cached master rows (by ID).
 */
export function enrichNomineesWithBizOpsCodes(nominees, apiCache = {}) {
  const salList = apiCache.salutationdetails?.data || [];
  const relList = apiCache.relationshipdetails?.data || [];
  const bankList = apiCache.bankdetails?.data || [];
  return (nominees || []).map((n) => {
    const sal = salList.find((s) => String(s.salutationID) === String(n.titleOfNominee));
    const rel = relList.find((r) => String(r.relationshipID) === String(n.relationship));
    // NomineeTab's own title/relationship dropdowns store the master's
    // display text (salutationTitle/relation), not its ID — so the raw
    // BizCode the backend also wants is looked up by that text instead.
    const salByTitle = salList.find(
      (s) => String(s.salutationTitle) === String(n.titleOfNominee),
    );
    const relByLabel = relList.find(
      (r) => String(r.relation) === String(n.relationship),
    );
    const appointeeSalByTitle = salList.find(
      (s) => String(s.salutationTitle) === String(n.appointeeTitle),
    );
    const appointeeRelByLabel = relList.find(
      (r) => String(r.relation) === String(n.appointeeRelationship),
    );
    const bank = bankList.find((b) => String(b.bankName) === String(n.bankName));
    const appointeeBank = bankList.find(
      (b) => String(b.bankName) === String(n.appointeeBankName),
    );
    return {
      ...n,
      nomineeSalutationCode: sal ? mapSalutationTitleToBizOps(sal.salutationTitle) : "",
      nomineeRelationshipCode: rel ? mapRelationLabelToBizOps(rel.relation) : "",
      titleOfNomineeLabel: salByTitle?.salutationTitle || n.titleOfNominee || "",
      salutationBizCode: salByTitle?.salutationBizCode || "",
      relationshipLabel: relByLabel?.relation || n.relationship || "",
      relationshipBizCode: relByLabel?.relationshipBizCode || "",
      appointeeTitleLabel: appointeeSalByTitle?.salutationTitle || n.appointeeTitle || "",
      appointeeSalutationBizCode: appointeeSalByTitle?.salutationBizCode || "",
      appointeeRelationshipLabel: appointeeRelByLabel?.relation || n.appointeeRelationship || "",
      appointeeRelationshipBizCode: appointeeRelByLabel?.relationshipBizCode || "",
      bankNameLabel: bank?.bankName || n.bankName || "",
      bankBizCode: bank?.bankBizCode || "",
      appointeeBankNameLabel: appointeeBank?.bankName || n.appointeeBankName || "",
      appointeeBankBizCode: appointeeBank?.bankBizCode || "",
      stateLabel: n.state || "",
      cityLabel: n.city || "",
      communicationStateLabel: n.communicationState || "",
      communicationCityLabel: n.communicationCity || "",
      appointeeStateLabel: n.appointeeState || "",
      appointeeCityLabel: n.appointeeCity || "",
    };
  });
}
